<template>
    <div class="change-cluster-infos">
        <!-- 이미지 영역 -->
        <div class="img-item-wrap">
            <div class="img-wrap">
                <img
                    :src="props.data.BeforeClusterInfo?.ClusterProfile_URL ?? ''"
                    :alt="t('trend_report_change_info_before_cluster_info_image_alt')"
                />
            </div>
            <div class="arrow-wrap">
                <div class="arrow-line-icon"></div>
            </div>
            <div
                class="img-wrap"
                :style="{ border: `5px solid ${computedNowClassColor}`, backgroundColor: computedNowClassColor }"
            >
                <img
                    :src="props.data.NowClusterInfo?.ClusterProfile_URL ?? ''"
                    :alt="t('trend_report_change_info_now_cluster_info_image_alt')"
                />
            </div>
        </div>
        <!-- 텍스트 영역 -->
        <div class="cluster-text-area">
            <div class="cluster-body-type-wrap">
                <span class="class-name" :style="{ color: computedBeforeClassColor }">
                    {{ props.data.BeforeClusterInfo?.BodyTypeName ?? '' }}
                </span>
                <span class="class-name" :style="{ color: computedNowClassColor }">
                    {{ props.data.NowClusterInfo?.BodyTypeName ?? '' }}
                </span>
            </div>
            <div class="cluster-class-name-wrap">
                <span class="class-type">
                    {{ props.data.BeforeClusterInfo?.ClusterName ?? '' }}
                </span>
                <span class="class-type">
                    {{ props.data.NowClusterInfo?.ClusterName ?? '' }}
                </span>
            </div>
            <div class="cluster-class-wrap">
                <div class="class-wrap">
                    <div class="class-wrap-inner">
                        <span class="class-label">Class</span>
                        <b class="class-level" :style="{ color: computedBeforeClassColor }">{{
                            props.data.BeforeClusterInfo?.BodyClass ?? ''
                        }}</b>
                    </div>
                </div>
                <!-- 변화 상태 텍스트 -->
                <p
                    class="change-state-text-box"
                    :style="{
                        background: props.data.ClassDiffCommentBackgroundColor ?? '',
                    }"
                >
                    {{ props.data.ClassDiffComment ?? '' }}
                </p>
                <div class="class-wrap">
                    <div class="class-wrap-inner">
                        <span class="class-label">Class</span>
                        <b class="class-level" :style="{ color: computedNowClassColor }">{{
                            props.data.NowClusterInfo?.BodyClass ?? ''
                        }}</b>
                    </div>
                </div>
            </div>
            <div class="cluster-rank-wrap">
                <div class="rank-wrap">
                    <div class="rank-wrap-inner">
                        <span class="rank-label">Rank</span>
                        <p class="rank-num-wrap">
                            <b>{{ props.data.BeforeClusterInfo?.Rank ?? '' }}</b>
                            <span>/{{ props.data.BeforeClusterInfo?.TotalNumOfCluster ?? '' }}</span>
                        </p>
                    </div>
                </div>
                <div class="change-rank-text-wrap">
                    <p class="arrow-icon" :class="computedChangeRankClass"></p>
                    <span class="change-rank-number">{{ Math.abs(computedChangeRankNumber) }}</span>
                </div>
                <div class="rank-wrap">
                    <div class="rank-wrap-inner">
                        <span class="rank-label">Rank</span>
                        <p class="rank-num-wrap">
                            <b>{{ props.data.NowClusterInfo?.Rank ?? '' }}</b>
                            <span>/{{ props.data.NowClusterInfo?.TotalNumOfCluster ?? '' }}</span>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { ClusterPannelGraph } from '@common/openapi-client/openapi';
import { getBodyTypeColor } from '@common/common/utils/utils';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps<{
    data: ClusterPannelGraph;
}>();

const computedChangeRankNumber = computed(() => {
    return (props.data.BeforeClusterInfo?.Rank ?? 0) - (props.data.NowClusterInfo?.Rank ?? 0);
});

const computedChangeRankClass = computed(() => {
    return computedChangeRankNumber.value > 0 ? 'increase' : 'decrease';
});

const computedNowClassColor = computed(() => {
    const NowClusterInfo = props.data.NowClusterInfo;
    const clusterTypeEnglishName = NowClusterInfo?.ClusterId?.split('_')[0] ?? '';
    const clusterGroup = NowClusterInfo?.ClusterId?.split('_')[2];

    return getBodyTypeColor(clusterTypeEnglishName, clusterGroup, false);
});

const computedBeforeClassColor = computed(() => {
    const BeforeClusterInfo = props.data.BeforeClusterInfo;
    const clusterTypeEnglishName = BeforeClusterInfo?.ClusterId?.split('_')[0] ?? '';
    const clusterGroup = BeforeClusterInfo?.ClusterId?.split('_')[2];

    return getBodyTypeColor(clusterTypeEnglishName, clusterGroup, false);
});
</script>

<style scoped lang="scss">
.change-cluster-infos {
    display: flex;
    flex-direction: column;
}

.img-wrap {
    width: 100%;
    aspect-ratio: 1 / 1;
    border-radius: 50%;
    box-sizing: border-box;
    margin-bottom: 0.3rem;
    border: 5px solid transparent;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 50%;
    }
}

.img-item-wrap {
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    z-index: 2;
}

.arrow-wrap {
    height: 100%;
    margin: 0 1.7rem 0 1.7rem;
    display: flex;
    align-items: center;
    justify-content: center;

    .arrow-line-icon {
        width: 3.1rem;
        height: 1.2rem;
        background: url('../../../../public/assets/images/ic_long_arrow_gray.png') no-repeat center center / 100% 100%;
    }
}

.cluster-text-area {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;

    > div {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        text-align: center;
        box-sizing: border-box;

        > .class-name,
        > .class-type,
        .class-wrap,
        .rank-wrap {
            display: inline-block;
            width: calc(50% - 32.5px);
            box-sizing: border-box;
            margin-bottom: 0.3rem;
        }

        .class-name {
            font-size: 1.4rem;
            font-weight: 700;
            line-height: 150%;
            background: $primary-white;
        }

        .class-type {
            font-size: 1.1rem;
            font-weight: 700;
            line-height: 150%;
            color: $grey-7;
            word-break: keep-all;
        }

        .class-wrap {
            display: inline-block;
            font-size: 1.4rem;
            line-height: 150%;

            .class-wrap-inner {
                display: inline-block;
                background: $primary-white;
                padding: 0 0.4rem;
                position: relative;
                z-index: 2;
            }

            .class-label {
                margin-right: 0.4rem;
            }
        }

        .rank-wrap {
            display: inline-flex;
            justify-content: center;
            font-size: 1.4rem;
            line-height: 150%;

            .rank-wrap-inner {
                display: inline-flex;
                justify-content: center;
                background: $primary-white;
                padding: 0 0.4rem;
                position: relative;
                z-index: 2;
            }

            .rank-label {
                margin-right: 0.4rem;
            }
            .rank-num-wrap {
                span {
                    font-size: 1.2rem;
                }
            }
        }
    }
}

.change-state-text-box {
    position: relative;
    padding: 0 0.5rem;
    background: $grey-1;
    border-radius: 0.5rem;
    font-size: 1.4rem;
    line-height: 150%;
    color: $primary-white;

    &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: calc(100% + 0.65rem);
        width: 7.5rem;
        height: 0.1rem;
        background: $grey-5;
        z-index: 1;
    }

    &::after {
        content: '';
        position: absolute;
        top: 50%;
        right: calc(100% + 0.65rem);
        width: 7.5rem;
        height: 0.1rem;
        background: $grey-5;
        z-index: 1;
    }
}
.change-rank-text-wrap {
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    line-height: 150%;
    margin-top: 0.2rem;

    &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: calc(100% + 0.65rem);
        width: 7.5rem;
        height: 0.1rem;
        background: $grey-5;
        z-index: 1;
    }

    &::after {
        content: '';
        position: absolute;
        top: 50%;
        right: calc(100% + 0.65rem);
        width: 7.5rem;
        height: 0.1rem;
        background: $grey-5;
        z-index: 1;
    }

    .arrow-icon {
        width: 0.8rem;
        height: 0.5rem;
        margin-right: 0.3rem;

        &.increase {
            background: url('../../../../public/assets/images/ic_arrow_green.png') no-repeat center center / 100% 100%;
        }
        &.decrease {
            background: url('../../../../public/assets/images/ic_arrow_red.png') no-repeat center center / 100% 100%;
        }
    }
    .change-rank-number {
        font-size: 1.4rem;
        font-weight: 700;
        line-height: 150%;
        color: $grey-7;
    }
}
</style>
